import {Injectable} from '@angular/core';
import {SegmentService} from 'ngx-segment-analytics';
import {LoggedInUserState} from '@shared/services/api/user/logged-in-user-state';

@Injectable()
export class UsageAnalyticsService {

  private readonly enabled: boolean;

  constructor(private segment: SegmentService, private loggedInUserState: LoggedInUserState) {
    this.enabled = true;
    // Segment debugging is extremely verbose
    segment.debug(false);
  }

  identify(userId?: string, traits?: any, options?: any): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.identify(userId, traits, options));
  }

  track(event: string, properties?: any, options?: any): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.track(event, properties, options));
  }

  /**
   * Tracking user email is only temporary, as engineers are using user email to feed into airtable (legacy kondo) via
   * zapier once the reporting migrates to amplitude completely, we will remove email from ticket and comment events.
   */
  trackAndIncludeUserEmail(event: string, properties?: any, options?: any): Promise<UsageAnalyticsService> {
    const userEmail = this.getLoggedInUserEmail();
    return this.proxyInvoke(() => this.segment.track(event,
      userEmail ? { ...properties, user_email: userEmail } : properties, options));
  }

  page(category?: string, name?: string, properties?: any, options?: any): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.page(category, name, properties, options));
  }

  group(groupId: string, traits?: any): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.group(groupId, traits));
  }

  alias(userId: string, previousId?: string, options?: any): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.alias(userId, previousId, options));
  }

  ready(): Promise<UsageAnalyticsService> {
    return this.proxyInvoke(() => this.segment.ready());
  }

  user(): any {
    return this.segment.user();
  }

  id(): string | null {
    return this.segment.id();
  }

  traits(): any {
    return this.segment.traits();
  }

  reset(): void {
    this.segment.reset();
  }

  debug(enabled?: boolean): void {
    this.segment.debug(enabled);
  }

  on(method: string, callback: (event?: string, properties?: any, options?: any) => any): void {
    this.segment.on(method, callback);
  }

  trackLink(elements: HTMLElement | HTMLElement[], event: string | Function, properties?: any | Function): void {
    this.segment.trackLink(elements, event, properties);
  }

  trackForm(forms: HTMLFormElement | HTMLFormElement[], event: string | Function, properties?: any | Function): void {
    this.segment.trackForm(forms, event, properties);
  }

  private proxyInvoke(fn: () => Promise<SegmentService>): Promise<UsageAnalyticsService> {
    if (this.enabled) {
      return new Promise((resolve, reject) => {
        fn().then(() => resolve(this)).catch(e => reject(e));
      });
    } else {
      return Promise.resolve(this);
    }
  }

  private getLoggedInUserEmail(): string {
    const loggedInUser = this.loggedInUserState.getLoggedInUser();
    return loggedInUser && loggedInUser.email;
  }
}
